import { delay } from "../client.js";
import { DEFAULT_SERVICE_ACCOUNT_PERMISSIONS, normalizeServiceAccountPermissions } from "../../pages/Accounts/serviceAccountUtils.js";

const STORAGE_KEY = "console-design.account-service-accounts.v2";

const SEED_SERVICE_ACCOUNTS = {
  "acc-1": [
    {
      id: "svc-1",
      name: "Production integration",
      publicKey: "pk_ac1_8f3a2b1c9d4e5f6a",
      privateKey: "sk_ac1_7e2f8a1b3c5d9e2f",
      permissions: { view: true, edit: true, delete: false },
      createdAt: "2025-01-15T09:00:00.000Z",
      updatedAt: "2025-03-10T14:20:00.000Z",
      lastRotatedAt: "2025-03-10T14:20:00.000Z",
    },
    {
      id: "svc-2",
      name: "Staging webhook",
      publicKey: "pk_ac1_2c4e6a8b0d1f3a5c",
      privateKey: "sk_ac1_1b3d5f7a9c0e2d4f",
      permissions: { view: true, edit: false, delete: false },
      createdAt: "2025-02-20T11:30:00.000Z",
      updatedAt: "2025-02-20T11:30:00.000Z",
      lastRotatedAt: null,
    },
  ],
};

function cloneSeed() {
  return Object.fromEntries(
    Object.entries(SEED_SERVICE_ACCOUNTS).map(([id, rows]) => [id, rows.map((row) => ({ ...row }))]),
  );
}

function loadStore() {
  const merged = cloneSeed();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return merged;
    const parsed = JSON.parse(raw);
    for (const [accountId, rows] of Object.entries(parsed)) {
      if (Array.isArray(rows)) merged[accountId] = rows;
    }
  } catch {
    /* ignore */
  }
  return merged;
}

function saveStore(store) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
}

function nextId() {
  return `svc-${Date.now().toString(36)}`;
}

function randomSegment(length = 16) {
  const chars = "abcdefghijklmnopqrstuvwxyz0123456789";
  let out = "";
  for (let i = 0; i < length; i += 1) out += chars[Math.floor(Math.random() * chars.length)];
  return out;
}

function generateKeyPair(accountId) {
  const suffix = accountId.replace(/[^a-z0-9]/gi, "").slice(-4).toLowerCase() || "acct";
  const segment = randomSegment(16);
  return {
    publicKey: `pk_${suffix}_${segment}`,
    privateKey: `sk_${suffix}_${randomSegment(16)}`,
  };
}

function withNormalizedPermissions(row) {
  return {
    ...row,
    permissions: normalizeServiceAccountPermissions(row.permissions),
  };
}

function getAccountServiceAccounts(store, accountId) {
  if (!store[accountId]) {
    store[accountId] = SEED_SERVICE_ACCOUNTS[accountId]
      ? SEED_SERVICE_ACCOUNTS[accountId].map((row) => withNormalizedPermissions({ ...row }))
      : [];
  }
  return store[accountId];
}

function validateName(name, rows, excludeId) {
  const trimmed = name?.trim();
  if (!trimmed) throw new Error("Service account name is required");
  if (rows.some((r) => r.id !== excludeId && r.name.toLowerCase() === trimmed.toLowerCase())) {
    throw new Error("A service account with this name already exists");
  }
  return trimmed;
}

export async function listAccountServiceAccounts(accountId) {
  await delay(120);
  const store = loadStore();
  return getAccountServiceAccounts(store, accountId).map((row) => withNormalizedPermissions(row));
}

export async function createAccountServiceAccount(accountId, payload) {
  await delay(140);
  const store = loadStore();
  const rows = getAccountServiceAccounts(store, accountId);
  const name = validateName(payload.name, rows);
  const keys = generateKeyPair(accountId);
  const now = new Date().toISOString();
  const permissions = normalizeServiceAccountPermissions(payload.permissions ?? DEFAULT_SERVICE_ACCOUNT_PERMISSIONS);
  const row = { id: nextId(), name, ...keys, permissions, createdAt: now, updatedAt: now, lastRotatedAt: null };
  rows.push(row);
  saveStore(store);
  return { ...row };
}

export async function updateAccountServiceAccount(accountId, serviceAccountId, payload) {
  await delay(120);
  const store = loadStore();
  const rows = getAccountServiceAccounts(store, accountId);
  const row = rows.find((r) => r.id === serviceAccountId);
  if (!row) throw new Error("Service account not found");
  row.name = validateName(payload.name, rows, serviceAccountId);
  row.permissions = normalizeServiceAccountPermissions(payload.permissions ?? row.permissions);
  row.updatedAt = new Date().toISOString();
  saveStore(store);
  return { ...row };
}

export async function rotateAccountServiceAccountKeys(accountId, serviceAccountId) {
  await delay(150);
  const store = loadStore();
  const rows = getAccountServiceAccounts(store, accountId);
  const row = rows.find((r) => r.id === serviceAccountId);
  if (!row) throw new Error("Service account not found");
  const keys = generateKeyPair(accountId);
  row.publicKey = keys.publicKey;
  row.privateKey = keys.privateKey;
  const now = new Date().toISOString();
  row.updatedAt = now;
  row.lastRotatedAt = now;
  saveStore(store);
  return { ...row };
}

export async function deleteAccountServiceAccount(accountId, serviceAccountId) {
  await delay(100);
  const store = loadStore();
  const rows = getAccountServiceAccounts(store, accountId);
  const index = rows.findIndex((r) => r.id === serviceAccountId);
  if (index < 0) throw new Error("Service account not found");
  rows.splice(index, 1);
  saveStore(store);
}
